/**
 * Responsive-image helpers for the photo strips.
 *
 * The banners render into short bands — a 1067x1600 frame paints at 149x224 on
 * a phone — so shipping the full file to every device is several times more
 * pixels than the screen can resolve. `scripts/gen-image-variants.sh` writes
 * `<name>-<width>w.webp` next to each source; these helpers point srcset at
 * them and let the browser choose.
 *
 * Keep the width lists here in sync with that script.
 */

/** Widths generated per folder — must match gen-image-variants.sh. */
// 300w was measured and dropped: it is what a DPR-2 phone would pick at
// 149 CSS px, and it visibly softened the detailed florals. 450 covers
// DPR 2 and 3 at that size for ~1% difference from the source.
export const BAND_WIDTHS = [450, 640];
export const WIDE_WIDTHS = [400, 800, 1200];

/**
 * Builds a srcset from a `.webp` source plus its generated variants, ending
 * with the original at its true width so large screens can still reach it.
 *
 * `intrinsicWidth` matters: a variant wider than the original is never
 * generated, so listing one would 404. Anything >= the original is dropped.
 */
export function srcSetFor(
  src: string,
  widths: number[],
  intrinsicWidth: number,
): string {
  const base = src.replace(/\.webp$/, "");
  const entries = widths
    .filter((w) => w < intrinsicWidth)
    .map((w) => `${base}-${w}w.webp ${w}w`);
  return [...entries, `${src} ${intrinsicWidth}w`].join(", ");
}

/**
 * `sizes` for a height-constrained frame: the CSS height is fixed per
 * breakpoint and the width follows from the aspect ratio, so the displayed
 * width is `height * (w / h)`. Browsers need that width, not the height.
 *
 * `steps` is ordered widest-breakpoint-first, as the sizes syntax requires.
 */
export function sizesForHeights(
  aspect: number,
  steps: { minWidth?: number; height: number }[],
): string {
  return steps
    .map(({ minWidth, height }) => {
      const w = Math.round(height * aspect);
      return minWidth ? `(min-width: ${minWidth}px) ${w}px` : `${w}px`;
    })
    .join(", ");
}
